import { WaypointType } from '@/types/waypoint';
import type {
  WaypointMetadata,
  BlindMetadata,
  FoodSourceMetadata,
  CameraMetadata,
  WaterSourceMetadata,
  BeddingAreaMetadata,
  GameTrailMetadata,
} from '@/types/waypoint';

import { BlindForm } from '@/components/waypoint/forms/BlindForm';
import { FoodSourceForm } from '@/components/waypoint/forms/FoodSourceForm';
import { CameraForm } from '@/components/waypoint/forms/CameraForm';
import { WaterSourceForm } from '@/components/waypoint/forms/WaterSourceForm';
import { BeddingAreaForm } from '@/components/waypoint/forms/BeddingAreaForm';
import { GameTrailForm } from '@/components/waypoint/forms/GameTrailForm';
import { AccessPointForm } from '@/components/waypoint/forms/AccessPointForm';
import { GenericForm } from '@/components/waypoint/forms/GenericForm';

interface WaypointMetadataFormProps {
  type: WaypointType;
  metadata: Partial<WaypointMetadata>;
  onChange: (updated: Partial<WaypointMetadata>) => void;
  /** The property this waypoint belongs to — threaded to BlindForm for wind bearing. */
  propertyId: string;
  /** Waypoint pin coordinates — passed to forms that need an embedded mini map. */
  centerCoords?: [number, number];
  /** Current waypoint name — passed to GameTrailForm to round-trip through TrailDrawPage. */
  locationName?: string;
  /** When true, hides the wind compass in BlindForm (used on detail page where it appears in its own section). */
  hideWindCompass?: boolean;
}

// CAST comments are justified: the discriminated union type is narrowed by the enclosing switch case,
// but TypeScript cannot infer this through the generic Partial<WaypointMetadata>.

export function WaypointMetadataForm({ type, metadata, onChange, propertyId, centerCoords, locationName, hideWindCompass }: WaypointMetadataFormProps) {
  switch (type) {
    case WaypointType.FoodSource:
      return (
        <FoodSourceForm
          metadata={metadata as Partial<FoodSourceMetadata>} // CAST: narrowed by FoodSource case
          onChange={(updated) => onChange(updated)}
          centerCoords={centerCoords}
        />
      );

    case WaypointType.Camera:
      return (
        <CameraForm
          metadata={metadata as Partial<CameraMetadata>} // CAST: narrowed by Camera case
          onChange={(updated) => onChange(updated)}
        />
      );

    case WaypointType.WaterSource:
      return (
        <WaterSourceForm
          metadata={metadata as Partial<WaterSourceMetadata>} // CAST: narrowed by WaterSource case
          onChange={(updated) => onChange(updated)}
        />
      );

    case WaypointType.BeddingArea:
      return (
        <BeddingAreaForm
          metadata={metadata as Partial<BeddingAreaMetadata>} // CAST: narrowed by BeddingArea case
          onChange={(updated) => onChange(updated)}
        />
      );

    case WaypointType.GameTrail:
      return (
        <GameTrailForm
          metadata={metadata as Partial<GameTrailMetadata>} // CAST: narrowed by GameTrail case
          onChange={(updated) => onChange(updated as Partial<WaypointMetadata>)}
          centerCoords={centerCoords}
          locationName={locationName}
        />
      );

    case WaypointType.Blind:
      return (
        <BlindForm
          metadata={metadata as Partial<BlindMetadata>} // CAST: narrowed by Blind case
          onChange={(updated) => onChange(updated)}
          propertyId={propertyId}
          hideWindCompass={hideWindCompass}
        />
      );

    case WaypointType.AccessPoint:
      return (
        <AccessPointForm
          metadata={metadata as { type?: 'access_point'; accessNotes?: string }} // CAST: narrowed by AccessPoint case
          onChange={(updated) => onChange(updated as Partial<WaypointMetadata>)}
        />
      );

    case WaypointType.ObservationPoint:
    case WaypointType.Other:
    default:
      return (
        <GenericForm
          metadata={metadata as { type?: string; generalNotes?: string }} // CAST: narrowed by Other/default case
          onChange={(updated) => onChange(updated as Partial<WaypointMetadata>)}
        />
      );
  }
}
